import { ApolloError } from "@apollo/client";
import { useState, useCallback, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { IMeetingTypeValues, IMeetingItem } from "../RCAMeetingTypesSettingInterfaces";
import { FormikState } from "formik";
import { MeetingTypeEntity } from "entities/RCA/MeetingTypeEntity/MeetingTypeEntity";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import { LanguageCodeMap } from "constants/constants";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";

export const useCreateEditMeetingActions = () => {
    const { t, i18n } = useTranslation();
    const { corporations } = useContext(CorporateContext);
    const [typeToEdit, setTypeToEdit] = useState<IMeetingItem | null>(null);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarErrorMessage, setSnackbarErrorMessage] = useState("");
    const [deleteMeeting, setDeleteMeeting] = useState<IMeetingItem | null>(null);

    const { meetingTypes, refetch } = MeetingTypeEntity.GetMeetingTypes();
    const { addMeetingType } = MeetingTypeEntity.InsertMeetingType();
    const { changeMeetingType } = MeetingTypeEntity.UpdateMeetingType();
    const { updateTranslatioName } = FailureDetailsEntity.UpdateTranslationName();
    const { insertTranslatioName } = FailureDetailsEntity.InsertTranslationName();
    const { getExistingTranslationId } = FailureDetailsEntity.CheckForExistingTranslationId();

    const language = useMemo(() => i18n.language.slice(0, 2), [i18n.language]);
    const languageIdSelected = useMemo(() => LanguageCodeMap[language as keyof typeof LanguageCodeMap], [language]);

    const defaultTypes = useMemo(() => meetingTypes.filter((type) => type.default_translation), [meetingTypes]);
    const customTypes = useMemo(() => meetingTypes.filter((type) => type.translation), [meetingTypes]);

    const errorMessage = useCallback(
        (error?: ApolloError) => {
            let message = "";

            if (error?.message.includes("unique_name_translation_corporate")) {
                message = t("rca.settings.meeting_settings.existing_name_error");
            } else if (
                error?.message.includes("translation_name_name_language_id_corporate_id_key") ||
                error?.message.includes("translation_name_name_language_id_corporate_id_key")
            ) {
                message = t("rca.settings.meeting_settings.existing_name_translation_error");
            } else {
                message = t("rca.settings.meeting_settings.generic_error");
            }

            return message;
        },
        [t],
    );

    const handleAddMeetingTypeSubmit = useCallback(
        async (
            values: IMeetingTypeValues,
            helpers?: {
                resetForm?: (nextState?: Partial<FormikState<IMeetingTypeValues>> | undefined) => void;
                setSubmitting?: (isSubmitting: boolean) => void;
            },
        ) => {
            helpers?.setSubmitting?.(true);
            const trimmedName = values.name.trim();
            const translationNameValues = {
                ...(typeToEdit && { translation_id: typeToEdit?.translation?.id }),
                language_id: languageIdSelected,
                name: trimmedName,
                corporate_id: corporations[0].corporate_id,
            };

            if (typeToEdit) {
                try {
                    let response;
                    const existingTranslationId = await getExistingTranslationId(translationNameValues);
                    const existingTypeTranslationInSelectedLanguage = typeToEdit?.translation?.translation_names.find(
                        (tn) => tn.language_id === languageIdSelected,
                    );
                    if (existingTranslationId && existingTranslationId !== typeToEdit?.translation?.id) {
                        response = await changeMeetingType({
                            variables: {
                                id: typeToEdit.id,
                                changes: { name_translation_id: existingTranslationId },
                            },
                        });
                    } else if (existingTypeTranslationInSelectedLanguage) {
                        response = await updateTranslatioName({
                            variables: {
                                translation_id: typeToEdit?.translation?.id,
                                language_id: languageIdSelected,
                                changes: { name: trimmedName },
                            },
                        });
                    } else {
                        response = await insertTranslatioName({
                            variables: {
                                translation: translationNameValues,
                            },
                        });
                    }
                    if (response) {
                        setSnackbarMessage(t("rca.settings.meeting_settings.success_update"));
                        helpers?.resetForm?.();
                        helpers?.setSubmitting?.(false);
                        refetch();
                        setTypeToEdit(null);
                    }
                } catch (error) {
                    helpers?.setSubmitting?.(false);
                    setSnackbarErrorMessage(errorMessage(error as ApolloError));
                    return;
                }
            } else {
                const existingTranslationId = await getExistingTranslationId(translationNameValues);
                const meetingTypeValues = {
                    corporate_id: corporations[0].corporate_id,
                    ...(existingTranslationId
                        ? { name_translation_id: existingTranslationId }
                        : {
                              translation: {
                                  data: {
                                      translation_names: {
                                          data: translationNameValues,
                                      },
                                  },
                              },
                          }),
                };
                try {
                    const response = await addMeetingType({ variables: { meetingType: [meetingTypeValues] } });
                    if (response) {
                        setSnackbarMessage(t("rca.settings.meeting_settings.success_creation"));
                        helpers?.resetForm?.();
                        helpers?.setSubmitting?.(false);
                        refetch();
                    }
                } catch (error) {
                    console.log(error);
                    setSnackbarErrorMessage(errorMessage(error as ApolloError));
                    helpers?.setSubmitting?.(false);
                    return;
                }
            }
            helpers?.setSubmitting?.(false);
        },
        [
            languageIdSelected,
            corporations,
            typeToEdit,
            errorMessage,
            getExistingTranslationId,
            insertTranslatioName,
            updateTranslatioName,
            changeMeetingType,
            addMeetingType,
            refetch,
            t,
        ],
    );

    const getInitFormValues = (name: string = "") => {
        return {
            name,
        };
    };

    return {
        handleAddMeetingTypeSubmit,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        refetch,
        setDeleteMeeting,
        setTypeToEdit,
        getInitFormValues,
        deleteMeeting,
        typeToEdit,
        snackbarMessage,
        snackbarErrorMessage,
        defaultTypes,
        customTypes,
        languageIdSelected,
    };
};
